import React from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faTag,
  faLayerGroup,
  faDollarSign,
  faStore,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faEdit,
  faReceipt,
  faBoxes,
  faClipboardList,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { salesService } from "../../../services/service/salesService";

const SALE_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30, 50, 100, 250];

const SaleDetailModal = ({ sale, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = React.useState("product");
  const [salesPage, setSalesPage] = React.useState(1);
  const [salesPageSize, setSalesPageSize] = React.useState(5);
  const [salesSearch, setSalesSearch] = React.useState("");
  const [salesStartDate, setSalesStartDate] = React.useState("");
  const [salesEndDate, setSalesEndDate] = React.useState("");

  const [fetchedSales, setFetchedSales] = React.useState([]);
  const [isFetchingSales, setIsFetchingSales] = React.useState(false);

  const stockId = sale?.stockId;

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!stockId) {
        setFetchedSales([]);
        return;
      }

      setIsFetchingSales(true);
      try {
        const response = await salesService.getAll({
          stockId,
          per_page: 500,
        });
        const payload = response?.data?.data ?? response?.data ?? null;
        const rows = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
        if (!cancelled) setFetchedSales(rows);
      } catch (err) {
        console.error("Failed to load sales for stock:", err);
        if (!cancelled) setFetchedSales([]);
      } finally {
        if (!cancelled) setIsFetchingSales(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [stockId]);

  // Sort oldest → newest, compute per-sale delta, revenue and profit,
  // then reverse to newest-first for display.
  const sellerRows = React.useMemo(() => {
    const rows = Array.isArray(fetchedSales) ? fetchedSales.slice() : [];
    if (rows.length === 0) return [];

    const chronological = rows.sort((a, b) => {
      const at = new Date(a.saleDate || a.created_at || 0).getTime();
      const bt = new Date(b.saleDate || b.created_at || 0).getTime();
      return at - bt;
    });

    let previousRemaining = null;

    const withDeltas = chronological.map((row) => {
      const remaining = Number(row.no_of_unsold ?? 0);
      const totalItems = Number(row.totalItemsInStock ?? 0);

      const perSaleSold =
        previousRemaining === null
          ? Math.max(0, totalItems - remaining)
          : Math.max(0, previousRemaining - remaining);

      previousRemaining = remaining;

      const pricePerItem = Number(row.actualSellingPrice ?? 0);
      const costPerItem = Number(row.costPerItem ?? 0);

      const perSaleRevenue = perSaleSold * pricePerItem;
      const perSaleProfit = perSaleSold * (pricePerItem - costPerItem);

      return {
        ...row,
        perSaleSold,
        perSaleRevenue,
        perSaleProfit,
      };
    });

    return withDeltas.reverse();
  }, [fetchedSales]);

  const isGrouped = sellerRows.length > 0;

  const filteredSellers = React.useMemo(() => {
    if (!isGrouped) return [];

    const term = salesSearch.trim().toLowerCase();

    return sellerRows.filter((row) => {
      if (term) {
        const haystack = [row.sellerName, row.sellerEmail, row.sellerPhone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      if (salesStartDate || salesEndDate) {
        const rawDate = row.saleDate || row.created_at;
        if (!rawDate) return false;
        const saleTime = new Date(rawDate).getTime();

        if (salesStartDate) {
          const startTime = new Date(salesStartDate + "T00:00:00").getTime();
          if (saleTime < startTime) return false;
        }
        if (salesEndDate) {
          const endTime = new Date(salesEndDate + "T23:59:59").getTime();
          if (saleTime > endTime) return false;
        }
      }

      return true;
    });
  }, [isGrouped, sellerRows, salesSearch, salesStartDate, salesEndDate]);

  const sortedSellers = React.useMemo(() => {
    return filteredSellers.slice().sort((a, b) => {
      const at = a.saleDate ? new Date(a.saleDate).getTime() : 0;
      const bt = b.saleDate ? new Date(b.saleDate).getTime() : 0;
      return bt - at;
    });
  }, [filteredSellers]);

  // Computed totals from the per-sale rows (fallback to the passed-in sale).
  const totals = React.useMemo(() => {
    const rows = Array.isArray(sellerRows) ? sellerRows : [];

    if (rows.length === 0) {
      return {
        totalItems: Number(sale?.totalItemsInStock ?? 0),
        itemsSold: Number(sale?.itemsSold ?? 0),
        unsoldItems: Number(sale?.no_of_unsold ?? 0),
        revenue: Number(sale?.actualTotalRevenue ?? 0),
        profit: Number(sale?.actualProfit ?? 0),
        netProfit:
          Number(sale?.actualProfit ?? 0) - Number(sale?.actualLoss ?? 0),
        costPerItem: Number(sale?.costPerItem ?? 0),
        pricePerItem: Number(sale?.actualPricePerItem ?? 0),
        totalCost: Number(sale?.totalCostOfSold ?? 0),
      };
    }

    let totalItems = 0;
    let itemsSold = 0;
    let revenue = 0;
    let profit = 0;
    let totalCost = 0;

    rows.forEach((row) => {
      totalItems = Math.max(totalItems, Number(row.totalItemsInStock ?? 0));
      itemsSold += Number(row.perSaleSold ?? 0);
      revenue += Number(row.perSaleRevenue ?? 0);
      profit += Number(row.perSaleProfit ?? 0);
      totalCost +=
        Number(row.perSaleSold ?? 0) * Number(row.costPerItem ?? 0);
    });

    const latestRow = rows[0] || {};
    const unsoldItems = Number(latestRow.no_of_unsold ?? 0);
    const latestCostPerItem = Number(latestRow.costPerItem ?? 0);
    const latestPricePerItem = Number(latestRow.actualSellingPrice ?? 0);

    return {
      totalItems,
      itemsSold,
      unsoldItems,
      revenue,
      profit,
      netProfit: profit,
      costPerItem: latestCostPerItem,
      pricePerItem: latestPricePerItem,
      totalCost,
    };
  }, [sellerRows, sale]);

  if (!sale) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0 TSH";
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateShort = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalItems = totals.totalItems;
  const itemsSold = totals.itemsSold;
  const unsoldItems = totals.unsoldItems;
  const costPerItem = totals.costPerItem;
  const pricePerItem = totals.pricePerItem;
  const totalCost = totals.totalCost;
  const revenue = totals.revenue;
  const profit = totals.profit;
  const netProfit = totals.netProfit;

  const totalSalesRows = sortedSellers.length;
  const totalSalesPages = Math.max(
    1,
    Math.ceil(totalSalesRows / salesPageSize),
  );
  const salesStartIndex = (salesPage - 1) * salesPageSize;
  const currentSalesRows = sortedSellers.slice(
    salesStartIndex,
    salesStartIndex + salesPageSize,
  );

  const handleSearchChange = (value) => {
    setSalesSearch(value);
    setSalesPage(1);
  };
  const handleStartDateChange = (value) => {
    setSalesStartDate(value);
    setSalesPage(1);
  };
  const handleEndDateChange = (value) => {
    setSalesEndDate(value);
    setSalesPage(1);
  };
  const clearDateFilter = () => {
    setSalesStartDate("");
    setSalesEndDate("");
    setSalesPage(1);
  };
  const handlePageSizeChange = (value) => {
    setSalesPageSize(Number(value));
    setSalesPage(1);
  };

  const InfoItem = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 ${
        highlight
          ? "bg-blue-50 border-blue-200"
          : "bg-gray-50 border-gray-100 hover:bg-gray-100"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          highlight ? "bg-blue-100" : "bg-white border border-gray-200"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`h-3.5 w-3.5 ${highlight ? "text-blue-600" : "text-gray-500"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-gray-600 font-medium">
          {label}
        </p>
        <p className="text-sm font-medium text-gray-900 truncate">
          {value || "-"}
        </p>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const normalized = (status || "ACTIVE").toUpperCase();
    const config = {
      ACTIVE: {
        className: "bg-white text-green-600 border-green-500",
        icon: faCheckCircle,
        iconColor: "text-green-600",
        label: "Active",
      },
      INACTIVE: {
        className: "bg-white text-red-700 border-red-200",
        icon: faTimesCircle,
        iconColor: "text-red-500",
        label: "Inactive",
      },
      SUSPENDED: {
        className: "bg-white text-orange-700 border-orange-200",
        icon: faClock,
        iconColor: "text-orange-500",
        label: "Suspended",
      },
      COMPLETED: {
        className: "bg-white text-blue-600 border-blue-500",
        icon: faCheckCircle,
        iconColor: "text-blue-600",
        label: "Completed",
      },
      PENDING: {
        className: "bg-white text-yellow-700 border-yellow-300",
        icon: faClock,
        iconColor: "text-yellow-600",
        label: "Pending",
      },
      CANCELLED: {
        className: "bg-white text-red-700 border-red-300",
        icon: faTimesCircle,
        iconColor: "text-red-500",
        label: "Cancelled",
      },
    };
    return config[normalized] || config.ACTIVE;
  };

  const status = getStatusBadge(sale?.status);

  const tabs = [
    { id: "product", label: "Product", icon: faBox },
    { id: "sales", label: "Sales Details", icon: faClipboardList },
  ];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Sale Details"
      maxWidth="sm:max-w-3xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              onEdit(sale);
            }}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6"
          >
            <FontAwesomeIcon icon={faEdit} />
            Edit Sale
          </Button>
        </div>
      }
    >
      {sale && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faReceipt}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600 font-semibold">
                {sale.productName || "Product"}
              </p>
              <p className="text-xs text-gray-500">
                {[sale.productType, sale.productCategory, sale.shopName]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
            </div>
            <Badge
              variant="outline"
              className={`${status.className} text-xs font-medium px-3 py-1 shrink-0`}
            >
              {status.label}
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 text-center border border-blue-200">
              <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold">
                Total Stock
              </p>
              <p className="text-sm font-bold text-gray-900">{totalItems}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 text-center border border-green-200">
              <p className="text-xs uppercase tracking-wider text-green-600 font-semibold">
                Total Sold
              </p>
              <p className="text-sm font-bold text-green-600">{itemsSold}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 text-center border border-orange-200">
              <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold">
                Remaining
              </p>
              <p className="text-sm font-bold text-orange-600">{unsoldItems}</p>
            </div>
            <div
              className={`bg-gradient-to-br rounded-lg p-3 text-center border ${
                netProfit > 0
                  ? "from-green-50 to-green-100 border-green-200"
                  : netProfit < 0
                    ? "from-red-50 to-red-100 border-red-200"
                    : "from-gray-50 to-gray-100 border-gray-200"
              }`}
            >
              <p
                className={`text-xs uppercase tracking-wider font-semibold ${
                  netProfit > 0
                    ? "text-green-600"
                    : netProfit < 0
                      ? "text-red-600"
                      : "text-gray-600"
                }`}
              >
                {netProfit > 0
                  ? "Profit"
                  : netProfit < 0
                    ? "Loss"
                    : "Break Even"}
              </p>
              <p
                className={`text-sm font-bold ${
                  netProfit > 0
                    ? "text-green-600"
                    : netProfit < 0
                      ? "text-red-600"
                      : "text-gray-600"
                }`}
              >
                {netProfit >= 0
                  ? formatCurrency(netProfit)
                  : `(${formatCurrency(Math.abs(netProfit))})`}
              </p>
            </div>
          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-blue-900 text-white shadow-sm"
                    : "bg-transparent text-gray-600 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "product" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b border-gray-200 pb-2">
                  <FontAwesomeIcon
                    icon={faBox}
                    className="text-blue-600 h-3.5 w-3.5"
                  />
                  Product Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <InfoItem
                    icon={faTag}
                    label="Product Name"
                    value={sale.productName}
                    highlight={true}
                  />
                  <InfoItem
                    icon={faLayerGroup}
                    label="Type / Category"
                    value={`${sale.productType || ""} ${
                      sale.productType && sale.productCategory ? "/" : ""
                    } ${sale.productCategory || ""}`}
                  />
                  <InfoItem
                    icon={faBoxes}
                    label="Package Type"
                    value={sale.packageType}
                  />
                  <InfoItem icon={faStore} label="Shop" value={sale.shopName} />
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b border-gray-200 pb-2">
                  <FontAwesomeIcon
                    icon={faDollarSign}
                    className="text-blue-600 h-3.5 w-3.5"
                  />
                  Financials
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <InfoItem
                    icon={faDollarSign}
                    label="Purchase Price (Total)"
                    value={formatCurrency(sale.purchasePrice)}
                  />
                  <InfoItem
                    icon={faDollarSign}
                    label="Cost Per Item"
                    value={formatCurrency(costPerItem)}
                  />
                  <InfoItem
                    icon={faDollarSign}
                    label="Price Per Item"
                    value={formatCurrency(pricePerItem)}
                    highlight={true}
                  />
                  <InfoItem
                    icon={faDollarSign}
                    label="Total Revenue"
                    value={formatCurrency(revenue)}
                    highlight={true}
                  />
                  <InfoItem
                    icon={faDollarSign}
                    label="Total Cost of Sold"
                    value={formatCurrency(totalCost)}
                  />
                  <InfoItem
                    icon={faDollarSign}
                    label="Total Profit"
                    value={formatCurrency(profit)}
                  />
                </div>
              </div>

              <div className={`rounded-md p-4 ${status.className} border`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon
                      icon={status.icon}
                      className={`h-5 w-5 ${status.iconColor}`}
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Status:{" "}
                        <span className={status.iconColor}>
                          {sale.status?.toUpperCase?.() || sale.status}
                        </span>
                      </p>
                      <p className="text-xs text-gray-600 font-medium">
                        Latest sale on {formatDate(sale.saleDate)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "sales" && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5"
                  />
                  <Input
                    type="text"
                    placeholder="Search by seller name, phone or email..."
                    value={salesSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-9 h-10 text-sm border-gray-300"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={salesStartDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="h-10 text-sm border-gray-300"
                  />
                  <span className="text-gray-500 text-sm">to</span>
                  <Input
                    type="date"
                    value={salesEndDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    className="h-10 text-sm border-gray-300"
                  />
                  {(salesStartDate || salesEndDate) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearDateFilter}
                      className="h-10 text-xs border-gray-300 hover:border-blue-800"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {isFetchingSales && (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-600 border-b border-gray-200 bg-gray-50">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="h-3 w-3 animate-spin"
                    />
                    Loading sales...
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          #
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Seller
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Date
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Sold
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Remaining
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Price/Item
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Revenue
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">
                          Profit
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentSalesRows.length > 0 ? (
                        currentSalesRows.map((row, idx) => {
                          const globalIdx = salesStartIndex + idx + 1;
                          const rowNet = Number(row.perSaleProfit ?? 0);

                          return (
                            <tr
                              key={row.salesId || idx}
                              className="hover:bg-blue-50/40 transition-colors"
                            >
                              <td className="py-2 px-3 text-xs text-gray-500 font-medium">
                                {globalIdx}
                              </td>
                              <td className="py-2 px-3 text-sm text-gray-800">
                                <div>
                                  <p className="font-medium">
                                    {row.sellerName || "N/A"}
                                  </p>
                                  {row.sellerPhone && (
                                    <p className="text-xs text-gray-500">
                                      {row.sellerPhone}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-3 text-sm text-gray-700 whitespace-nowrap">
                                {formatDateShort(row.saleDate)}
                              </td>
                              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-800">
                                {row.perSaleSold ?? 0}
                              </td>
                              <td className="py-2 px-3 text-right text-sm font-semibold text-orange-600">
                                {row.no_of_unsold ?? 0}
                              </td>
                              <td className="py-2 px-3 text-right text-sm text-gray-700 whitespace-nowrap">
                                {formatCurrency(row.actualSellingPrice)}
                              </td>
                              <td className="py-2 px-3 text-right text-sm font-semibold text-green-600 whitespace-nowrap">
                                {formatCurrency(row.perSaleRevenue)}
                              </td>
                              <td
                                className={`py-2 px-3 text-right text-sm font-semibold whitespace-nowrap ${
                                  rowNet > 0
                                    ? "text-green-600"
                                    : rowNet < 0
                                      ? "text-red-600"
                                      : "text-gray-600"
                                }`}
                              >
                                {rowNet > 0
                                  ? formatCurrency(rowNet)
                                  : rowNet < 0
                                    ? `(${formatCurrency(Math.abs(rowNet))})`
                                    : formatCurrency(0)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="py-8 text-center">
                            <div className="flex flex-col items-center gap-2 text-gray-400">
                              <FontAwesomeIcon
                                icon={faClipboardList}
                                className="h-8 w-8"
                              />
                              <p className="text-sm">
                                No sales found
                                {salesSearch || salesStartDate || salesEndDate
                                  ? " matching your filters"
                                  : ""}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalSalesRows > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                      <span>
                        Showing{" "}
                        <span className="font-semibold">
                          {salesStartIndex + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-semibold">
                          {Math.min(
                            salesStartIndex + salesPageSize,
                            totalSalesRows,
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold">{totalSalesRows}</span>{" "}
                        sales
                      </span>
                      <div className="flex items-center gap-1">
                        <span>Show</span>
                        <select
                          value={salesPageSize}
                          onChange={(e) => handlePageSizeChange(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 bg-white"
                        >
                          {SALE_PAGE_SIZE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <span>per page</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={salesPage === 1}
                        onClick={() => setSalesPage((p) => Math.max(1, p - 1))}
                        className="h-8 w-8 p-0 border-gray-300 hover:border-blue-800"
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="h-3 w-3"
                        />
                      </Button>
                      <span className="text-xs text-gray-700 px-2">
                        Page {salesPage} of {totalSalesPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={salesPage === totalSalesPages}
                        onClick={() =>
                          setSalesPage((p) => Math.min(totalSalesPages, p + 1))
                        }
                        className="h-8 w-8 p-0 border-gray-300 hover:border-blue-800"
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="h-3 w-3"
                        />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default SaleDetailModal;